
import { Injectable, Renderer2, RendererFactory2, inject, DOCUMENT } from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

import { IListOfferAvailableCredit } from 'src/app/pages/hiring/interfaces/validations.interface';
import { environment } from 'src/environments/environment';
import { TRACKING_IDS } from '../../constants/generic-headers.constants';
import { EventsService } from '../events/events.service';
import {
  TAGGIN_CONTENT_TYPE,
  TAGGIN_EVENT_CATEGORY,
  TAGGIN_EVENT_CURRENCY,
  TAGGIN_EVENT_FUNNEL,
  TAGGIN_EVENT_LIST,
  TAGGIN_EVENT_NAME,
  TAGGIN_EVENT_SUBFUNNEL,
} from './../../constants/event-tags.constant';

declare let fbq: Function | undefined;
declare let ttq: { track: (...args: unknown[]) => void } | undefined;

@Injectable({
  providedIn: 'root',
})
export class TagginService {
  private readonly document = inject(DOCUMENT);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly gtmService = inject(GoogleTagManagerService);
  private readonly eventsService = inject(EventsService);

  private readonly renderer: Renderer2;

  indexHome: number = 0;
  indexSelectPlan: number = 0;
  userEmail: string = '';
  userPhone: string = '';
  simType: string = '';
  originEvent: string = '';
  acceptCookies: string = '';

  pospaidHost = environment.containerHost;
  hostPospaidLocal = environment.pospaidHost;

  dataListOffersGeneral: IListOfferAvailableCredit[] = [];
  dataListOffers: IListOfferAvailableCredit[] = [];

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    const win = this.document.defaultView;
    if (win) {
      win.addEventListener('message', (event: MessageEvent) => {
        // deepcode ignore InsufficientPostmessageValidation: El origen se valida de forma segura contra la variable de entorno
        if (event.origin !== this.pospaidHost) {
          return;
        }

        if (typeof event.data === 'object' && event.data !== null) {
          if (event.data.type === 'acceptCookies') {
            this.acceptCookies = event.data.type;
          }
        }
      });
    }
  }

  private getSelectedItemPayload() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    if (!offer) return [];

    return [
      {
        item_id: offer.offeringId,
        item_name: offer.name,
        coupon: '',
        discount: 0,
        index: this.indexSelectPlan,
        item_category: TAGGIN_EVENT_CATEGORY.CATEGORY_NAME,
        price: offer.price,
        quantity: 1,
      },
    ];
  }

  private loadScriptTiktok(): void {
    const script = this.renderer.createElement('script');
    script.innerHTML = `!function (w, d, t) { w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"], ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++) ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++) ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=i+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)}; ttq.load('${TRACKING_IDS.tiktok}'); ttq.page(); }(window, document, 'ttq');`;
    this.renderer.appendChild(this.document.body, script);
  }

  private loadScriptMetaPixel(): void {
    const script = this.renderer.createElement('script');
    script.innerHTML = `!function(f,b,e,v,n,t,s) {if(f.fbq)return;n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)}; if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0'; n.queue=[];t=b.createElement(e);t.async=!0; t.src=v;s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s)}(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js'); fbq('init', '${TRACKING_IDS.meta}'); fbq('track', 'PageView');`;
    this.renderer.appendChild(this.document.body, script);
  }

  loadScripts() {
    if (this.document.defaultView) {
      this.loadScriptTiktok();
      this.loadScriptMetaPixel();
    }
  }

  pushTag(data: Record<string, unknown>) {
    this.gtmService.pushTag({ ecommerce: null });
    this.gtmService.pushTag(data);
  }

  pushOnlyTag(data: Record<string, unknown>) {
    this.gtmService.pushTag(data);
  }

  setMetaTag(metaTagOne: string, metaTagTwo?: any, metaTagThree?: any) {
    if (!this.isTaggin() || fbq === undefined) return;
    fbq(metaTagOne, metaTagTwo, metaTagThree);
  }

  setTiktokTag(ttTagOne: string, ttTagTwo?: any, ttTagThree?: any) {
    if (!this.isTaggin() || !ttq?.track) return;
    ttq.track(ttTagOne, ttTagTwo, ttTagThree);
  }

  isTaggin(): boolean {
    const win = this.document.defaultView;
    if (win && win.self !== win.top && !this.acceptCookies) {
      return false;
    }
    return true;
  }

  sendInformationTags(dataTag: any, tagType: string) {
    const win = this.document.defaultView;
    if (win && win.self === win.top) {
      if (tagType === 'gtmTagReset') {
        return this.pushTag(dataTag);
      }
      return this.pushOnlyTag(dataTag);
    }
    return this.eventsService.sendTags(tagType, dataTag);
  }

  gtmPospaidBtn() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_BTN,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_BTN,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_BTN,
      },
    };
  }

  pospaidMetaBtn() {
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_POSPAID_BTN_TRACKCUSTOM,
      TAGGIN_EVENT_NAME.META_POSPAID_BTN,
    );
  }

  pospaidTiktokBtn() {
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_POSPAID_BTN);
  }

  gtmViewItemsList() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_VIEW_ITEMS_LIST,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_VIEW_ITEMS_LIST,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.SUBFUNNEL_VIEW_ITEMS_LIST,
        items:
          this.dataListOffersGeneral?.map((offer, index) => ({
            item_id: offer.offeringId,
            item_name: offer.name,
            discount: 0,
            index: index,
            item_category: TAGGIN_EVENT_CATEGORY.CATEGORY_NAME,
            item_list_name: TAGGIN_EVENT_LIST.LIST_NAME,
            price: offer.price,
            quantity: 1,
          })) || [],
      },
    };
  }

  gtmSelectItem() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_SELECT_ITEM,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_SELECT_ITEMS,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.SUBFUNNEL_SELECT_ITEMS,
        items: [
          {
            item_id: this.dataListOffersGeneral[this.indexHome]?.offeringId,
            item_name: this.dataListOffersGeneral[this.indexHome]?.name,
            discount: 0,
            index: this.indexHome,
            item_category: TAGGIN_EVENT_CATEGORY.CATEGORY_NAME,
            item_list_name: TAGGIN_EVENT_LIST.LIST_NAME,
            promotion_name: this.dataListOffersGeneral[this.indexHome]?.name,
            creative_name: '',
            creative_slot: this.indexHome,
            price: this.dataListOffersGeneral[this.indexHome]?.price,
            quantity: 1,
          },
        ],
      },
    };
  }

  gtmPospaidPhoneValidation() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_PHONE_VALIDATION,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_PHONE_VALIDATION,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_PHONE_VALIDATION,
      },
    };
  }

  gtmPospaidNumber() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_NUMBER,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_NUMBER,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_NUMBER,
      },
    };
  }

  gtmPospaidNumberPin() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_NUMBER_PIN,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_NUMBER_PIN,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_NUMBER_PIN,
      },
    };
  }

  gtmPospaidBiometrics() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_BIOMETRICS,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_BIOMETRICS,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_BIOMETRICS,
      },
    };
  }

  gtmPospaidIdentifyValidation() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_IDENTIFY_VALIDATION,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_IDENTIFY_VALIDATION,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_IDENTIFY_VALIDATION,
      },
    };
  }

  gtmPospaidFrontPhoto() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_FRONT_PHOTO,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_FRONT_PHOTO,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_FRONT_PHOTO,
      },
    };
  }

  gtmPospaidBackPhoto() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_BACK_PHOTO,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_BACK_PHOTO,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_BACK_PHOTO,
      },
    };
  }

  gtmPospaidPersonalPhoto() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_PERSONAL_PHOTO,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_PERSONAL_PHOTO,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_PERSONAL_PHOTO,
      },
    };
  }

  gtmPospaidPhotoValidation() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_PHOTO_VALIDATION,
      ecommerce: {
        funnel: TAGGIN_EVENT_NAME.GTM_POSPAID_PHOTO_VALIDATION,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_PHOTO_VALIDATION,
      },
    };
  }

  gtmPospaidForm() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_FORM,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_FORM,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_FORM,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
      },
    };
  }

  pospaidMetaForm() {
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_POSPAID_TRACKCUSTOM,
      TAGGIN_EVENT_NAME.META_POSPAID_FORM,
    );
  }

  pospaidTiktokForm() {
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_POSPAID_FORM);
  }

  gtmPospaidCredit() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_CREDIT,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_CREDIT,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_CREDIT,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
      },
    };
  }

  gtmPospaidCreditPin() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_CREDIT_PIN,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_CREDIT_PIN,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_CREDIT_PIN,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
      },
    };
  }

  pospaidMetaCreditPin() {
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_POSPAID_CREDIT_PIN_TRACKCUSTOM,
      TAGGIN_EVENT_NAME.META_POSPAID_CREDIT_PIN,
    );
  }

  pospaidTiktokCreditPin() {
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_POSPAID_CREDIT_PIN);
  }

  gtmAddToCard() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_ADD_TO_CARD,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_ADD_TO_CARD,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.SUBFUNNEL_ADD_TO_CARD,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
        currency: TAGGIN_EVENT_CURRENCY,
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        items: this.getSelectedItemPayload(),
      },
    };
  }

  metaAddToCard() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_ADD_TO_CARD_TRACK,
      TAGGIN_EVENT_NAME.META_ADD_TO_CARD,
      {
        content_type: TAGGIN_CONTENT_TYPE.META_ADD_TO_CARD,
        funnel: TAGGIN_EVENT_FUNNEL.META_FUNNEL_ADD_TO_CARD,
        contents: offer
          ? [{ id: offer.offeringId, name: offer.name, quantity: 1 }]
          : [],
      },
    );
  }

  pospaidMetaAddPlan() {
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_POSPAID_ADD_PLAN_TRACKCUSTOM,
      `${TAGGIN_EVENT_NAME.META_POSPAID_ADD_PLAN} ${this.dataListOffers[this.indexSelectPlan]?.price || 0}`,
    );
  }

  pospaidTikTokAddToCard() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_ADD_TO_CARD, {
      value: offer?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_ADD_TO_CARD,
      contents: offer
        ? [
            {
              content_type: TAGGIN_CONTENT_TYPE.POSPAID_ADD_TO_CARD,
              content_id: offer.offeringId,
              content_name: offer.name,
              price: offer.price,
              quantity: 1,
            },
          ]
        : [],
    });
  }

  pospaidTikTokAddPlan() {
    this.setTiktokTag(
      `${TAGGIN_EVENT_NAME.TIKTOK_POSPAID_ADD_PLAN} ${this.dataListOffers[this.indexSelectPlan]?.price || 0}`,
    );
  }

  gtmBeginCheckout() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_BEGIN_CHECKOUT,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_BEGIN_CHECKOUT,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.SUBFUNNEL_BEGIN_CHECKOUT,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
        currency: TAGGIN_EVENT_CURRENCY,
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        items: this.getSelectedItemPayload(),
      },
    };
  }

  initiateMetaCheckout() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_INITIATE_CHECKOUT_TRACK,
      TAGGIN_EVENT_NAME.META_INITIATE_CHECKOUT,
      {
        content_type: TAGGIN_CONTENT_TYPE.META_INITIATE_CHECKOUT,
        funnel: TAGGIN_EVENT_FUNNEL.META_FUNNEL_INITIATE_CHECKOUT,
        contents: offer
          ? [{ id: offer.offeringId, name: offer.name, quantity: 1 }]
          : [],
      },
    );
  }

  initiateTiktokCheckout() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_INITIATE_CHECKOUT, {
      value: offer?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.TIKTOK_FUNNEL_INITIATE_CHECKOUT,
      contents: offer
        ? [
            {
              content_type: TAGGIN_CONTENT_TYPE.TIKTOK_INITIATE_CHECKOUT,
              content_id: offer.offeringId,
              content_name: offer.name,
              price: offer.price,
              cuantity: 1,
            },
          ]
        : [],
    });
  }

  gtmAddShippingInfo() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_ADD_SHIPPING_INFO,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_ADD_SHIPPING_INFO,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.SUBFUNNEL_ADD_SHIPPING_INFO,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
        currency: TAGGIN_EVENT_CURRENCY,
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        shipping_tier: '',
        items: this.getSelectedItemPayload(),
      },
    };
  }

  addMetaShippingInfo() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_ADD_SHIPPING_INFO_TRACK,
      TAGGIN_EVENT_NAME.META_ADD_SHIPPING_INFO,
      {
        content_type: TAGGIN_CONTENT_TYPE.META_ADD_SHIPPING_INFO,
        funnel: TAGGIN_EVENT_FUNNEL.META_ADD_SHIPPING_INFO,
        contents: offer
          ? [{ id: offer.offeringId, name: offer.name, quantity: 1 }]
          : [],
      },
    );
  }

  addTiktokShippingInfo() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_ADD_SHIPPING_INFO, {
      value: offer?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_ADD_SHIPPING_INFO,
      contents: offer
        ? [
            {
              content_type: TAGGIN_CONTENT_TYPE.TIKTOK_ADD_SHIPPING_INFO,
              content_id: offer.offeringId,
              content_name: offer.name,
              price: offer.price,
              cuantity: 1,
            },
          ]
        : [],
    });
  }

  gtmPospaidSelectPayment() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_SELECT_PAYMENT,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_SELECT_PAYMENT,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_SELECT_PAYMENT,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
        currency: TAGGIN_EVENT_CURRENCY,
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        shipping_tier: '',
        items: this.getSelectedItemPayload(),
      },
    };
  }

  gtmAddPaymentInfo() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_ADD_PAYMENT_INFO,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_ADD_PAYMENT_INFO,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.SUBFUNNEL_ADD_PAYMENT_INFO,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
        currency: TAGGIN_EVENT_CURRENCY,
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        shipping_tier: '',
        payment_type: this.originEvent,
        items: this.getSelectedItemPayload(),
      },
    };
  }

  addMetaPaymentInfo() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_ADD_PAYMENT_INFO_TRACK,
      TAGGIN_EVENT_NAME.META_ADD_PAYMENT_INFO,
      {
        content_type: TAGGIN_CONTENT_TYPE.META_ADD_PAYMENT_INFO,
        funnel: TAGGIN_EVENT_FUNNEL.META_FUNNEL_ADD_PAYMENT_INFO,
        contents: offer
          ? [{ id: offer.offeringId, name: offer.name, quantity: 1 }]
          : [],
      },
    );
  }

  pospaidMetaPayment() {
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_POSPAID_PAYMENT_TRACKCUSTOM,
      TAGGIN_EVENT_NAME.META_POSPAID_PAYMENT,
    );
  }

  addTiktokPaymentInfo() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_ADD_PAYMENT_INFO, {
      value: offer?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.TIKTOK_FUNNEL_ADD_PAYMENT_INFO,
      contents: offer
        ? [
            {
              content_type: TAGGIN_CONTENT_TYPE.TIKTOK_ADD_PAYMENT_INFO,
              content_id: offer.offeringId,
              content_name: offer.name,
              price: offer.price,
              cuantity: 1,
            },
          ]
        : [],
    });
  }

  pospaidTiktokPayment() {
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_POSPAID_PAYMENT);
  }

  gtmPurchase() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_PURCHASE,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.FUNNEL_PURCHASE,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.SUBFUNNEL_PURCHASE,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
        currency: TAGGIN_EVENT_CURRENCY,
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        shipping_tier: '',
        payment_type: this.originEvent,
        items: this.getSelectedItemPayload(),
      },
    };
  }

  purchaseMeta() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_PURCHASE_TRACK,
      TAGGIN_EVENT_NAME.META_PURCHASE,
      {
        transaction_id: '',
        value: offer?.price || 0,
        currency: TAGGIN_EVENT_CURRENCY,
        funnel: TAGGIN_EVENT_FUNNEL.META_FUNNEL_PURCHASE,
        contents: offer
          ? [{ id: offer.offeringId, name: offer.name, quantity: 1 }]
          : [],
      },
    );
  }

  pospaidMetaPurchase() {
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_POSPAID_PURCHASE_TRACKCUSTOM,
      TAGGIN_EVENT_NAME.META_POSPAID_PURCHASE,
      {
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        currency: TAGGIN_EVENT_CURRENCY,
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_META_PURCHASE,
      },
    );
  }

  completeTiktokPayment() {
    const offer = this.dataListOffers[this.indexSelectPlan];
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_COMPLETE_PAYMENT, {
      transaction_id: '',
      value: offer?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.TIKTOK_FUNNEL_COMPLETE_PAYMENT,
      contents: offer
        ? [
            {
              content_type: TAGGIN_CONTENT_TYPE.TIKTOK_COMPLETE_PAYMENT,
              content_id: offer.offeringId,
              content_name: offer.offeringId,
              price: offer.price,
              quantity: 1,
            },
          ]
        : [],
    });
  }

  pospaidTiktokPurchase() {
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_POSPAID_PURCHASE, {
      value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_TIKTOK_PURCHASE,
    });
  }

  gtmPospaidConfirm() {
    return {
      event: TAGGIN_EVENT_NAME.GTM_POSPAID_CONFIRM,
      ecommerce: {
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_CONFIRM,
        subfunnel: TAGGIN_EVENT_SUBFUNNEL.POSPAID_SUBFUNNEL_CONFIRM,
        user_email: this.userEmail,
        user_phone: this.userPhone,
        sim_type: this.simType,
        currency: TAGGIN_EVENT_CURRENCY,
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        shipping_tier: '',
        payment_type: this.originEvent,
        items: this.getSelectedItemPayload(),
      },
    };
  }

  pospaidMetaConfirm() {
    this.setMetaTag(
      TAGGIN_EVENT_NAME.META_POSPAID_CONFIRM_TRACKCUSTOM,
      TAGGIN_EVENT_NAME.META_POSPAID_CONFIRM,
      {
        value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
        currency: TAGGIN_EVENT_CURRENCY,
        funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_META_CONFIRM,
      },
    );
  }

  pospaidTiktokConfirm() {
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_POSPAID_CONFIRM, {
      value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.POSPAID_FUNNEL_TIKTOK_CONFIRM,
    });
  }

  rechargeTiktokConfirm() {
    this.setTiktokTag(TAGGIN_EVENT_NAME.TIKTOK_RECHARGE_CONFIRM, {
      value: this.dataListOffers[this.indexSelectPlan]?.price || 0,
      currency: TAGGIN_EVENT_CURRENCY,
      funnel: TAGGIN_EVENT_FUNNEL.TIKTOK_FUNNEL_RECHARGE_CONFIRM,
    });
  }
}
